import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StewChat } from "@/components/stew/StewChat";
import { StewHistoryDrawer } from "@/components/stew/StewHistoryDrawer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
// Transparent seal from public/icons (chroma-keyed cream/white to alpha).
const stewSealIcon = "/icons/seal-transparent.png";


/**
 * Standalone Stew surface at /stew.
 *
 * Full member Stew (real StewChat, surface="member") with no portal chrome:
 * no MainLayout, no top nav, no bottom tab bar. Just a slim header (logo,
 * "Stew", History + New) and the chat owning the rest of the viewport.
 *
 * Hides the ask_a_person and library tool chips so this surface never routes
 * members off Stew. The Support hub is untouched.
 */

// MACHINERY: localStorage key for the one-time intro tooltip on /stew.
// Do not rename. Bump the version suffix if the intro copy or targeting rules
// meaningfully change and every member should see it again.
const INTRO_SEEN_KEY_PREFIX = "stew.standalone.intro.seen.v1:";
const INTRO_DELAY_MS = 30_000;
const INTRO_AUTO_DISMISS_MS = 15_000;

function firstNameFrom(fullName: string | null | undefined): string | null {
  if (!fullName) return null;
  const first = fullName.trim().split(/\s+/)[0];
  return first && first.length > 0 ? first : null;
}

export default function StewStandalone() {
  const { user } = useAuth();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [introVisible, setIntroVisible] = useState(false);
  const introCancelledRef = useRef(false);
  const surfaceRef = useRef<HTMLDivElement>(null);

  const seenKey = user ? `${INTRO_SEEN_KEY_PREFIX}${user.id}` : null;

  // Load member first name for the greeting.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();
      if (!cancelled) setFirstName(firstNameFrom(data?.full_name));
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Schedule the one-time intro tooltip.
  useEffect(() => {
    if (!seenKey) return;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(seenKey)) return;

    introCancelledRef.current = false;
    const surface = surfaceRef.current;

    const cancelPending = () => {
      introCancelledRef.current = true;
      window.localStorage.setItem(seenKey, "1");
    };

    // Any typing or click inside the Stew surface before the tooltip appears
    // (composer input, send, mic tap, opening History) counts as engagement.
    const onKey = () => cancelPending();
    const onPointer = (e: Event) => {
      // Ignore clicks on nothing (e.g. background of a sheet already closed).
      if (!e.target) return;
      cancelPending();
    };
    surface?.addEventListener("keydown", onKey, true);
    surface?.addEventListener("pointerdown", onPointer, true);

    const showTimer = window.setTimeout(() => {
      if (introCancelledRef.current) return;
      window.localStorage.setItem(seenKey, "1");
      setIntroVisible(true);
    }, INTRO_DELAY_MS);

    return () => {
      window.clearTimeout(showTimer);
      surface?.removeEventListener("keydown", onKey, true);
      surface?.removeEventListener("pointerdown", onPointer, true);
    };
  }, [seenKey]);

  // Auto-dismiss the tooltip after it's been visible for a while.
  useEffect(() => {
    if (!introVisible) return;
    const t = window.setTimeout(() => setIntroVisible(false), INTRO_AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [introVisible]);

  const greeting = firstName
    ? `Hi ${firstName}, I'm Stew. I can help you with your questions about Stewardship.`
    : "Hi, I'm Stew. I can help you with your questions about Stewardship.";

  // Set the browser tab title while this page is mounted; restore on unmount.
  useEffect(() => {
    const prev = document.title;
    document.title = "Meet Stew";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div
      ref={surfaceRef}
      className="flex flex-col viewport-gold-backdrop"
      style={{
        height: "100dvh",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <header className="shrink-0 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[860px] items-center justify-between gap-2 px-3 md:px-4 h-14">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={stewSealIcon}
              alt="Wealthy Steward"
              width={28}
              height={28}
              className="h-7 w-7 rounded-md"
            />
            <span className="text-base md:text-lg font-semibold tracking-tight">
              Meet Stew
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveConversationId(null)}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New
            </Button>
            <StewHistoryDrawer
              activeId={activeConversationId}
              onSelect={setActiveConversationId}
              refreshToken={historyRefreshToken}
            />
          </div>
        </div>
      </header>

      <main className="relative flex-1 min-h-0 overflow-hidden">
        {/*
          Desktop: chat floats as a card on the gradient with breathing room.
          Mobile: the card runs edge-to-edge below the header per the
          native-feel rule (rule 8), keeping the composer within thumb reach.
        */}
        <div className="mx-auto h-full w-full max-w-[860px] px-0 pt-0 pb-0 md:px-4 md:pt-5 md:pb-5">
          <div className="h-full overflow-hidden bg-background md:rounded-2xl md:border md:border-border/60 md:shadow-sm">
            <StewChat
              conversationId={activeConversationId}
              onConversationCreated={(id) => {
                setActiveConversationId(id);
                setHistoryRefreshToken((n) => n + 1);
              }}
              onConversationTouched={() =>
                setHistoryRefreshToken((n) => n + 1)
              }
              hiddenToolKeys={["ask_a_person", "library"]}
              fillContainer
            />
          </div>
        </div>


        {introVisible && (
          <IntroTooltip
            message={greeting}
            onDismiss={() => setIntroVisible(false)}
          />
        )}
      </main>

      <footer className="shrink-0 px-3 py-1.5 text-center text-[11px] leading-tight text-muted-foreground">
        <span>&copy; {new Date().getFullYear()} Wealthy Steward</span>
        <span className="mx-2" aria-hidden="true">&middot;</span>
        <Link to="/privacy" className="inline-block py-1 px-1 underline underline-offset-2 hover:text-foreground">
          Privacy
        </Link>
        <span className="mx-1" aria-hidden="true">&middot;</span>
        <Link to="/terms" className="inline-block py-1 px-1 underline underline-offset-2 hover:text-foreground">
          Terms
        </Link>
      </footer>
    </div>
  );
}

function IntroTooltip({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      // Anchored just above the composer. pointer-events-none on the wrapper
      // so the composer stays fully tappable; the bubble itself re-enables
      // pointer events. Safe-area inset keeps it above the iPhone home bar.
      className="pointer-events-none absolute inset-x-0 z-40 flex justify-center px-4"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        className="pointer-events-auto relative max-w-[22rem] rounded-2xl bg-primary text-primary-foreground shadow-lg pl-4 pr-12 py-3 text-left text-sm leading-snug animate-fade-in"
      >
        <span>{message}</span>
        <span
          aria-hidden="true"
          // Small tail pointing down toward the composer.
          className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 bg-primary"
        />
        <span
          role="button"
          tabIndex={-1}
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute top-1 right-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-primary-foreground/80 hover:text-primary-foreground"
        >
          <X className="h-4 w-4" />
        </span>
      </button>
    </div>
  );
}
